import { useContext, useCallback } from 'react';
import { AbilityContext } from 'context/Can';
export * as permissions from 'static/Permission';

/**
 * @param {Array<{action: string, subject: string}>} permissions //? example permissions = [{ action: 'VIEW', subject: 'FOLDER' }, { action: 'EDIT', subject: 'FOLDER', }]
 * @returns {boolean}
 */
export const useCheckPermissions = (permissions) => {
  const ability = useContext(AbilityContext);
  for (const permission of permissions) {
    if (ability.can(`${permission.action}`, `${permission.subject}`)) {
      return true;
    }
  }
  return false;
};

export const useCheckPermissionOR = () => {
  const ability = useContext(AbilityContext);

  const isValidPermissionOR = useCallback(
    (permissions) => {
      if (permissions) {
        if (Array.isArray(permissions?.[0])) {
          for (const permission of permissions) {
            if (ability.can(permission?.[0], permission?.[1])) {
              return true;
            }
          }
        } else {
          return ability.can(permissions?.[0], permissions?.[1]);
        }
      }
      return false;
    },
    [ability]
  );

  return isValidPermissionOR;
};

export const useCheckPermissionAnd = () => {
  const ability = useContext(AbilityContext);

  const isValidPermissionAND = useCallback(
    (permissions) => {
      let allowed = true;

      if (permissions) {
        if (Array.isArray(permissions?.[0])) {
          for (const permission of permissions) {
            if (ability.can(permission?.[0], permission?.[1])) {
              allowed *= true;
            } else {
              allowed *= false;
            }
          }
        } else {
          return ability.can(permissions?.[0], permissions?.[1]);
        }
      }

      return allowed ? true : false;
    },
    [ability]
  );

  return isValidPermissionAND;
};

/**
 * Check a single SECURE_CONFIG object: { PASS: ['ACTION', 'SUBJECT'] } or { PASS: [['ACTION', 'SUBJECT'], ...] }
 * PASS with nested arrays → AND logic (user must have ALL permissions)
 * PASS with single array → single permission check
 */
const checkSinglePermission = (rules, permission) => {
  const permissionToCheck = permission.PASS;

  if (Array.isArray(permissionToCheck[0])) {
    // AND: { PASS: [['EDIT', 'COMPANY_PROFILE'], ['EDIT', 'MEMBER']] }
    return permissionToCheck.every((p) =>
      rules.some(
        (rule) =>
          rule.action?.toLowerCase() === p[0]?.toLowerCase() &&
          rule.subject?.toLowerCase() === p[1]?.toLowerCase()
      )
    );
  }

  // Single: { PASS: ['VIEW', 'PRODUCT'] }
  return rules.some(
    (rule) =>
      rule.action?.toLowerCase() === permissionToCheck[0]?.toLowerCase() &&
      rule.subject?.toLowerCase() === permissionToCheck[1]?.toLowerCase()
  );
};

/**
 * @param {Array} rules - user permission rules from defineRulesFor + defineRulesForWorkflow
 * @param {Object|Array} permissions - single SECURE_CONFIG object or array of SECURE_CONFIG objects
 * @param {'or'|'and'} operator - default 'or'. Array of configs uses OR logic unless 'and' is passed
 * @returns {boolean}
 *
 * @example
 * Single
 * checkPermissionOnRoute(rules, SECURE_CONFIG.PRODUCT)
 *
 * OR (default) — user needs at least one
 * checkPermissionOnRoute(rules, [SECURE_CONFIG.USER, SECURE_CONFIG.COMPANY_PROFILE])
 *
 * AND — user needs all
 * checkPermissionOnRoute(rules, [SECURE_CONFIG.PRODUCT, SECURE_CONFIG.ACCESS_SYNDICATION], 'and')
 */
export const checkPermissionOnRoute = ({
  rules,
  permissions,
  operator = 'or',
}) => {
  if (!rules || !permissions) return false;

  // Super admin bypasses everything
  const hasSuperAdminAccess = rules.some(
    (rule) =>
      rule.action?.toLowerCase() === 'manage' &&
      rule.subject?.toLowerCase() === 'all'
  );
  if (hasSuperAdminAccess) return true;

  // OR operator as default for array of permissions
  if (Array.isArray(permissions)) {
    return operator === 'and'
      ? permissions.every((config) => checkSinglePermission(rules, config))
      : permissions.some((config) => checkSinglePermission(rules, config));
  }

  // Single SECURE_CONFIG object: { PASS: [...] }
  if (permissions.PASS) {
    return checkSinglePermission(rules, permissions);
  }

  return false;
};
